import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Package, Search, CheckCircle2, Truck, Clock, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrackOrder = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(searchParams.get('id') || '');
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isSpanish = i18n.language === 'es';

  const statusSteps = [
    { id: 1, key: 'received', en: 'Order Received', es: 'Pedido Recibido', icon: CheckCircle2 },
    { id: 2, key: 'processing', en: 'In Production', es: 'En Producción', icon: Clock },
    { id: 3, key: 'completed', en: 'Ready', es: 'Listo', icon: Package },
    { id: 4, key: 'delivered', en: 'Delivered', es: 'Entregado', icon: Truck }
  ];

  const handleTrack = async () => {
    if (!orderNumber.trim()) {
      setError(isSpanish ? 'Ingresa un número de pedido' : 'Please enter an order number');
      return;
    }

    setLoading(true);
    setError('');
    setOrderData(null);

    try {
      const response = await fetch(`${API_URL}/api/track/${orderNumber.trim()}`);
      if (!response.ok) {
        throw new Error(isSpanish ? 'Pedido no encontrado' : 'Order not found');
      }
      const data = await response.json();
      setOrderData(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTrack();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500 rounded-full mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800">
            {isSpanish ? 'Rastrear Pedido' : 'Track Your Order'}
          </h1>
          <p className="text-slate-600 mt-2">
            {isSpanish 
              ? 'Ingresa tu número de pedido para ver el estado' 
              : 'Enter your order number to check status'}
          </p>
        </div>

        {/* Search Box */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex gap-3">
              <Input
                placeholder={isSpanish ? 'Número de pedido (ej: ABC12345)' : 'Order number (e.g., ABC12345)'}
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                className="flex-1 text-lg"
                data-testid="order-number-input"
              />
              <Button 
                onClick={handleTrack} 
                disabled={loading}
                className="bg-cyan-500 hover:bg-cyan-600 px-6"
                data-testid="track-order-btn"
              >
                <Search className="w-5 h-5 mr-2" />
                {loading ? '...' : (isSpanish ? 'Buscar' : 'Track')}
              </Button>
            </div>
            {error && (
              <p className="text-red-500 text-sm mt-3 text-center">{error}</p>
            )}
          </CardContent>
        </Card>

        {/* Order Status */}
        {orderData && (
          <Card data-testid="order-status-card">
            <CardHeader className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center justify-between">
                <span>{isSpanish ? 'Pedido' : 'Order'} #{orderData.order_number}</span>
                <span className="text-sm font-normal opacity-90">
                  {isSpanish ? 'Fecha' : 'Date'}: {orderData.created_at}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between mb-2">
                  {statusSteps.map((step, idx) => {
                    const Icon = step.icon;
                    const isActive = orderData.progress >= step.id;
                    const isCurrent = orderData.progress === step.id;
                    
                    return (
                      <div key={step.id} className="flex flex-col items-center flex-1">
                        <div className={`
                          w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all
                          ${isActive 
                            ? 'bg-cyan-500 text-white' 
                            : 'bg-slate-200 text-slate-400'}
                          ${isCurrent ? 'ring-4 ring-cyan-200 scale-110' : ''}
                        `}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <span className={`text-xs text-center ${isActive ? 'text-cyan-600 font-medium' : 'text-slate-400'}`}>
                          {isSpanish ? step.es : step.en}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Progress line */}
                <div className="relative h-2 bg-slate-200 rounded-full mt-4">
                  <div 
                    className="absolute h-full bg-cyan-500 rounded-full transition-all duration-500"
                    style={{ width: `${((orderData.progress - 1) / 3) * 100}%` }}
                  />
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-cyan-500" />
                  {isSpanish ? 'Productos' : 'Products'}
                </h3>
                <div className="space-y-2">
                  {orderData.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white p-3 rounded border">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-slate-500">
                          {isSpanish ? 'Tamaño' : 'Size'}: {item.size}
                        </p>
                      </div>
                      <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm font-medium">
                        x{item.quantity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Estimated Completion */}
              {orderData.estimated_completion && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                  <Clock className="w-6 h-6 text-emerald-600" />
                  <div>
                    <p className="text-sm text-emerald-700">
                      {isSpanish ? 'Fecha estimada de completado' : 'Estimated completion date'}
                    </p>
                    <p className="font-medium text-emerald-800">{orderData.estimated_completion}</p>
                  </div>
                </div>
              )}

              {/* Contact */}
              <div className="mt-6 text-center">
                <p className="text-slate-500 text-sm">
                  {isSpanish ? '¿Preguntas sobre tu pedido?' : 'Questions about your order?'}
                </p>
                <a 
                  href="https://wa.me/17282091041" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  <MapPin className="w-4 h-4" />
                  {isSpanish ? 'Contactar por WhatsApp' : 'Contact via WhatsApp'}
                </a>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
